'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword
} from 'firebase/auth';
import { useUserStore } from '../../store/useUserStore';
import { detectAuthProvider } from '../../utils/authUtils';

// Configuración de Firebase (debes agregar tus credenciales)
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || "AIzaSyCS-xG-VuEFGihuDDKS_0Rb-TSjg-_bIO0",
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || "hiperalia-cert.firebaseapp.com",
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "hiperalia-cert",
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || "hiperalia-cert.firebasestorage.app",
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || "748074347239",
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || "1:748074347239:web:7bd610a92ad95327b21042"
};

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Proveedores de autenticación
const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  syncWithPrestaShop: (userData: any) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { setIsLogged, setProfile, clearUserData } = useUserStore();

  // Sincronizar con PrestaShop
  const syncWithPrestaShop = async (userData: any, currentUser?: FirebaseUser) => {
    try {
      // Usar el usuario pasado como parámetro o el del estado
      const userToSync = currentUser || user;
      
      // Verificar que el usuario esté autenticado
      if (!userToSync) {
        return;
      }

      const idToken = await userToSync.getIdToken();
      if (!idToken) {
        return;
      }

      const response = await fetch('/api/auth/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: userToSync.uid,
          email: userData.email || userToSync.email,
          displayName: userToSync.displayName,
          firstName: userData.firstName,
          lastName: userData.lastName,
          provider: userData.provider || 'email'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error en respuesta de sincronización:', errorData);
        throw new Error(`Error al sincronizar con PrestaShop: ${response.status}`);
      }

      const data = await response.json();

      setProfile({
        firstName: data.data.firstname,
        lastName: data.data.lastname,
        email: data.data.email,
        id: data.data.id,
        provider: userData.provider
      });

    } catch (error) {
      console.error('Error al sincronizar con PrestaShop:', error);
      // No lanzar el error para evitar interrumpir el flujo de autenticación
    }
  };

  // Autenticación con email y contraseña
  const signIn = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Extraer nombre y apellido del displayName o email
      const displayName = user.displayName || '';
      const [firstName, ...lastNameParts] = displayName.split(' ');
      const lastName = lastNameParts.join(' ') || '';

      // Actualizar el estado local del usuario inmediatamente
      setUser(user);

      // Detectar proveedor de autenticación
      const authProvider = detectAuthProvider(user);

      // Actualizar store local
      setIsLogged(true);
      

      // Sincronizar con PrestaShop (no bloquear si falla)
      syncWithPrestaShop({
        firstName: firstName || 'Usuario',
        lastName: lastName || 'Ejemplo',
        email: user.email || '',
        provider: 'email'
      }, user).catch(error => {
        console.error('Error en sincronización (no crítico):', error);
      });
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  };

  // Registro con email y contraseña
  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Actualizar displayName
      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`
      });

      // Actualizar el estado local del usuario inmediatamente
      setUser(user);

      // Detectar proveedor de autenticación
      const authProvider = detectAuthProvider(user);

      // Actualizar store local
      setIsLogged(true);

      // Sincronizar con PrestaShop - DEBE completarse antes de continuar
      // Este es un paso crítico para el registro, por lo que esperamos a que termine
      await syncWithPrestaShop({
        firstName,
        lastName,
        email: user.email || '',
        provider: authProvider,
      }, user);
      
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      throw error;
    }
  };

  // Autenticación con Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      // Extraer nombre y apellido
      const firstName = user.displayName?.split(' ')[0] || 'Usuario';
      const lastName = user.displayName?.split(' ').slice(1).join(' ') || firstName;

      // Detectar proveedor de autenticación
      const authProvider = detectAuthProvider(user);

      // Actualizar store local
      setIsLogged(true);
      // Sincronizar con PrestaShop (no bloquear si falla)
      syncWithPrestaShop({
        firstName,
        lastName,
        email: user.email || '',
        provider: authProvider
      }, user).catch(error => {
        console.error('Error en sincronización (no crítico):', error);
      });
    } catch (error: any) {
      console.error('Error al iniciar sesión con Google:', error);
      
      // Manejar errores específicos de popup
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Inicio de sesión cancelado por el usuario');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('El popup fue bloqueado. Permite popups para este sitio');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('Ya existe una cuenta con este email usando otro método de inicio de sesión');
      }
      
      throw error;
    }
  };

  // Autenticación con Facebook
  const signInWithFacebook = async () => {
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      const user = result.user;
      
      // Extraer nombre y apellido
      const firstName = user.displayName?.split(' ')[0] || 'Usuario';
      const lastName = user.displayName?.split(' ').slice(1).join(' ') || 'Facebook';

      // Detectar proveedor de autenticación
      const authProvider = detectAuthProvider(user);

      // Actualizar store local
      setIsLogged(true);

      // Sincronizar con PrestaShop (no bloquear si falla)
      syncWithPrestaShop({
        firstName,
        lastName,
        email: user.email || '',
        provider: authProvider
      }, user).catch(error => {
        console.error('Error en sincronización (no crítico):', error);
      });
    } catch (error: any) {
      console.error('Error al iniciar sesión con Facebook:', error);
      
      // Manejar errores específicos de popup
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Inicio de sesión cancelado por el usuario');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('El popup fue bloqueado. Permite popups para este sitio');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('Ya existe una cuenta con este email usando otro método de inicio de sesión');
      }
      
      throw error;
    }
  };

  // Autenticación con Apple
  const signInWithApple = async () => {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;
      
      // Extraer nombre y apellido
      const firstName = user.displayName?.split(' ')[0] || 'Usuario';
      const lastName = user.displayName?.split(' ').slice(1).join(' ') || 'Apple';

      // Detectar proveedor de autenticación
      const authProvider = detectAuthProvider(user);

      // Actualizar store local
      setIsLogged(true);

      // Sincronizar con PrestaShop (no bloquear si falla)
      syncWithPrestaShop({
        firstName,
        lastName,
        email: user.email || '',
        provider: authProvider
      }, user).catch(error => {
        console.error('Error en sincronización (no crítico):', error);
      });
    } catch (error: any) {
      console.error('Error al iniciar sesión con Apple:', error);
      
      // Manejar errores específicos de popup
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Inicio de sesión cancelado por el usuario');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('El popup fue bloqueado. Permite popups para este sitio');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('Ya existe una cuenta con este email usando otro método de inicio de sesión');
      }
      
      throw error;
    }
  };

  // Cerrar sesión
  const logout = async () => {
    try {
      // Actualizar el estado local inmediatamente antes de llamar a signOut
      setUser(null);
      clearUserData(); // Esto limpia isLogged, profile, customerId y addresses
      
      // Luego cerrar sesión en Firebase
      await signOut(auth);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      throw error;
    }
  };

  // Escuchar cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      
      setUser(user);
      setLoading(false);

      if (user) {
        // Usuario autenticado
        // Solo actualizar el perfil si no tenemos información válida o si el displayName ha cambiado
        const currentProfile = useUserStore.getState().profile;
        const displayName = user.displayName || '';
        const firstName = displayName.split(' ')[0] || 'Usuario';
        const lastName = displayName.split(' ').slice(1).join(' ') || 'Ejemplo';

        // Detectar proveedor de autenticación
        const authProvider = detectAuthProvider(user);

        // Solo actualizar si no tenemos perfil o si el displayName ha cambiado o si no tenemos proveedor
        if (!currentProfile || currentProfile.firstName !== firstName || currentProfile.lastName !== lastName || !currentProfile.provider) {
          setIsLogged(true);
          setProfile({
            firstName,
            lastName,
            email: user.email || '',
            provider: authProvider
          });
        } else {
          // Solo asegurar que está logueado
          setIsLogged(true);
        }

        // No sincronizar automáticamente usuarios existentes para evitar errores
        // La sincronización se hará cuando el usuario inicie sesión activamente
      } else {
        // Usuario no autenticado - limpiar todos los datos
        clearUserData();
      }
    });

    return () => unsubscribe();
  }, [setIsLogged, setProfile, clearUserData]);

  // Función para cambiar la contraseña
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!user || !user.email) {
        throw new Error('Usuario no autenticado');
      }

      // Crear credenciales con el email actual y la contraseña actual
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      // Reautenticar al usuario
      await reauthenticateWithCredential(user, credential);

      // Actualizar la contraseña
      await updatePassword(user, newPassword);

    } catch (error: any) {
      console.error('Error al cambiar la contraseña:', error);
      
      // Manejar errores específicos
      if (error.code === 'auth/wrong-password') {
        throw new Error('La contraseña actual es incorrecta');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('La nueva contraseña es demasiado débil');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Por favor, vuelve a iniciar sesión para cambiar tu contraseña');
      }
      
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    logout,
    syncWithPrestaShop,
    changePassword
  };

  return (
    <AuthContext.Provider data-component-id="src/components/providers/AuthProvider.tsx:428:4" data-component-path="src/components/providers/AuthProvider.tsx" data-component-line="428" data-component-file="AuthProvider.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>
      {children}
    </AuthContext.Provider>
  );
}
